import { useState } from 'react';
import { useApiTokens, useCreateApiToken, useRevokeApiToken } from '@/hooks/useIssueMemory';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Copy, KeyRound } from 'lucide-react';
import { formatDate, formatRelativeTime } from '@/utils/helpers';

/**
 * Tokens for the Claude Code plugin (rv-issue-memory) and for agent accounts.
 */
export default function AccessTokens() {
  const { user } = useAuth();
  const isExecutive = user?.role === 'executive';
  const { data: tokens = [], isLoading } = useApiTokens();
  const createToken = useCreateApiToken();
  const revokeToken = useRevokeApiToken();

  const [name, setName] = useState('');
  const [readOnly, setReadOnly] = useState(false);
  const [expiresInDays, setExpiresInDays] = useState('180');
  const [agentUsername, setAgentUsername] = useState('');
  const [runner, setRunner] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The API (and its MCP endpoint) is served under /api on the dashboard's own origin
  const serverUrl = window.location.origin;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      const result = await createToken.mutateAsync({
        name: name.trim(),
        kind: agentUsername ? 'agent' : 'person',
        username: agentUsername || undefined,
        readOnly,
        runner: agentUsername ? runner : undefined,
        expiresInDays: expiresInDays ? parseInt(expiresInDays, 10) : undefined,
      });
      setCreated(result.token);
      setName('');
      setAgentUsername('');
    } catch (err) {
      const message = (err as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message;
      setError(message || (err as Error).message);
    }
  };

  const setup = created
    ? [
        `$env:RV_MEMORY_URL = '${serverUrl}'`,
        `$env:RV_MEMORY_TOKEN = '${created}'`,
        'claude plugin marketplace add <path to ai-management-assistant clone>',
        'claude plugin install rv-issue-memory@royalvision',
      ].join('\n')
    : '';

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Access tokens</h1>
        <p className="text-sm text-muted-foreground">
          For the Claude Code plugin <span className="font-mono">rv-issue-memory</span>: sessions on an issue branch start with
          that issue's memory and record what they did. A token acts as you; revoke it if a machine is lost.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">New token</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label htmlFor="token-name">Name</Label>
              <Input id="token-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="laptop" required className="w-56" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="token-expiry">Expires after (days)</Label>
              <Input id="token-expiry" type="number" min={1} max={730} value={expiresInDays} onChange={(e) => setExpiresInDays(e.target.value)} className="w-32" />
            </div>
            {isExecutive && (
              <div className="space-y-1">
                <Label htmlFor="token-agent">Agent account (optional)</Label>
                <Input id="token-agent" value={agentUsername} onChange={(e) => setAgentUsername(e.target.value)} placeholder="rv-dev" className="w-40" />
              </div>
            )}
            <label className="flex items-center gap-2 pb-2 text-sm">
              <input type="checkbox" checked={readOnly} onChange={(e) => setReadOnly(e.target.checked)} />
              Read-only
            </label>
            {isExecutive && agentUsername && (
              <label className="flex items-center gap-2 pb-2 text-sm" title="For the agent runner on the test server: claims AI turns; it cannot read or write issue memory">
                <input type="checkbox" checked={runner} onChange={(e) => setRunner(e.target.checked)} />
                Runner token
              </label>
            )}
            <Button type="submit" disabled={createToken.isPending || !name.trim()}>
              <KeyRound className="mr-1 h-4 w-4" />
              Create
            </Button>
          </form>
          {error && <p className="mt-3 text-sm text-red-600">{error}</p>}
          {created && (
            <div className="mt-4 space-y-2 rounded-md border border-green-200 bg-green-50 p-3 text-sm">
              <p className="font-medium">Copy it now; it is not shown again.</p>
              <div className="flex items-center gap-2">
                <code className="break-all rounded bg-background px-2 py-1 text-xs">{created}</code>
                <Button type="button" variant="outline" size="sm" onClick={() => navigator.clipboard.writeText(created)}>
                  <Copy className="h-3 w-3" />
                </Button>
              </div>
              <p className="text-muted-foreground">Then, where you run Claude Code (PowerShell):</p>
              <pre className="whitespace-pre-wrap rounded bg-background p-2 text-xs">{setup}</pre>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">{isExecutive ? 'All tokens' : 'Your tokens'}</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <LoadingSpinner />
          ) : tokens.length === 0 ? (
            <p className="text-sm text-muted-foreground">No tokens yet.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="py-1">Name</th>
                  <th>Acts as</th>
                  <th>Access</th>
                  <th>Created</th>
                  <th>Last used</th>
                  <th>Expires</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {tokens.map((t) => (
                  <tr key={t.id} className="border-t">
                    <td className="py-2">
                      {t.name} <span className="font-mono text-xs text-muted-foreground">{t.hint}…</span>
                    </td>
                    <td>
                      @{t.username}
                      {t.kind === 'agent' && <span className="ml-1 rounded bg-muted px-1 text-xs">agent</span>}
                    </td>
                    <td>{t.scopes.includes('memory:write') ? 'read + write' : 'read'}</td>
                    <td>{formatDate(t.createdAt)}</td>
                    <td>{t.lastUsedAt ? formatRelativeTime(t.lastUsedAt) : 'never'}</td>
                    <td>{t.expiresAt ? formatDate(t.expiresAt) : 'never'}</td>
                    <td className="text-right">
                      {t.revokedAt ? (
                        <span className="text-xs text-muted-foreground">revoked</span>
                      ) : (
                        <Button variant="outline" size="sm" disabled={revokeToken.isPending} onClick={() => revokeToken.mutate(t.id)}>
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
